// Which target languages the chat "Save vocabulary" popup offers, and which
// one is preselected (9.2-E; tutor capture #825 phase 2). Pure so it can be
// unit-tested without React.
//
// Student viewer:
// - A thread with a tutor defaults to that tutor's teaching language; the
//   student's other learning languages follow as alternatives.
// - The founder support thread is not a tutoring relationship even when the
//   support account has a tutor profile: only the student's own learning
//   languages are offered, so the card lands in "<Language> Personal".
//
// Tutor viewer:
// - Exactly one option — the tutor's own teaching language — and only when the
//   other participant is a student: the server creates the entry for that
//   student, in this language, attributed to the tutor (SaveVocabularyEntry's
//   tutor path ignores the `language` argument, but the chip still tells the
//   tutor where the card goes).
// - Never in the support thread (no student there), never without a teaching
//   language, and never when the server says it would refuse
//   (Conversation.tutorCanCapture: no booked lesson between the two yet, a
//   block, …). The action used to show in every student thread and be
//   refused on tap.
//
// No options at all → the caller hides the action (nothing could be saved).

export interface CaptureLanguage {
  id: string;
  name: string;
  code: string;
}

interface Args {
  viewerRole: string | undefined;
  // The viewer's own teaching language when they are a tutor; null otherwise.
  viewerTeachingLanguage: CaptureLanguage | null;
  studentLanguages: CaptureLanguage[];
  otherParticipant: { tutorProfile?: { teachingLanguage: CaptureLanguage } | null } | null | undefined;
  otherParticipantRole: string | undefined;
  isSupportThread: boolean;
  // Conversation.tutorCanCapture — meaningful for a tutor viewer only.
  tutorCanCapture: boolean;
}

export interface CaptureLanguageChoice {
  options: CaptureLanguage[];
  defaultCode: string | null;
}

const NONE: CaptureLanguageChoice = { options: [], defaultCode: null };

export function captureLanguageOptions({
  viewerRole,
  viewerTeachingLanguage,
  studentLanguages,
  otherParticipant,
  otherParticipantRole,
  isSupportThread,
  tutorCanCapture,
}: Args): CaptureLanguageChoice {
  if (viewerRole === "tutor") {
    if (isSupportThread || otherParticipantRole !== "student" || !viewerTeachingLanguage || !tutorCanCapture) return NONE;
    return { options: [viewerTeachingLanguage], defaultCode: viewerTeachingLanguage.code };
  }
  if (viewerRole !== "student") return NONE;

  const tutorLanguage = isSupportThread ? null : otherParticipant?.tutorProfile?.teachingLanguage ?? null;
  const seen = new Set<string>();
  const options: CaptureLanguage[] = [];
  for (const language of [tutorLanguage, ...studentLanguages]) {
    if (!language || seen.has(language.code)) continue;
    seen.add(language.code);
    options.push(language);
  }
  return { options, defaultCode: options[0]?.code ?? null };
}
